import React, { useState, useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { FaBuilding, FaIndianRupeeSign, FaLocationDot } from "react-icons/fa6";
import Canonical from "../components/Canonical.js";
import { Link } from "react-router-dom";
import Popup from "../components/Popup.jsx";

ChartJS.register(ArcElement, Tooltip, Legend);

const EmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(3.75);
  const [tenure, setTenure] = useState(10); // in years

  // Convert years to months
  const tenureMonths = tenure * 12;

  // EMI Calculation
  const emi = useMemo(() => {
    const P = loanAmount;
    const r = interestRate / 100 / 12;
    const n = tenureMonths;
    if (!P || !r || !n) return 0;
    const numerator = P * r * Math.pow(1 + r, n);
    const denominator = Math.pow(1 + r, n) - 1;
    return denominator === 0 ? 0 : numerator / denominator;
  }, [loanAmount, interestRate, tenureMonths]);

  const totalPayable = useMemo(() => emi * tenureMonths, [emi, tenureMonths]);
  const totalInterest = useMemo(
    () => totalPayable - loanAmount,
    [totalPayable, loanAmount],
  );

  const pieData = {
    labels: ["Principal Amount", "Interest Amount"],
    datasets: [
      {
        data: [Math.round(loanAmount), Math.round(totalInterest)],
        backgroundColor: ["#032b5f", "#d43f26"],
        hoverOffset: 3,
      },
    ],
  };

  // handle popup button
  const [popupData, setPopupData] = useState("");

  const handleButtonClick = (dataName) => {
    // console.log(dataName);
    setPopupData(dataName);
  };

  const canonicalUrl = `https://homesdekho.com/emi-calculator`;
  const metaTitle =
    "EMI Calculator | HomesDekho - Plan Your Home Loan with Ease";
  const hrfLangTag = `https://homesdekho.com/emi-calculator`;
  const description =
    "Use HomesDekho's EMI Calculator to estimate monthly installments, interest costs, and repayment schedules. Simple, accurate, and designed to make your home loan planning easier.";

  return (
    <>
      <Canonical
        canonicalUrl={canonicalUrl}
        title={metaTitle}
        hrfLang={hrfLangTag}
        description={description}
      />
      <div className="container">
        <div className="row mt-3">
          <div className="col-md-12 text-center">
            <div>
              <h1>HomesDekho EMI Calculator</h1>
            </div>
          </div>
        </div>
        <div className="row m-5">
          <div className="col-md-6">
            <div
              className="card p-4 shadow-lg"
              style={{
                width: "100%",
                background: "linear-gradient(#f8e1f4, #e5d4ef)",
              }}
            >
              <h4 className="text-center mb-4">EMI Calculator</h4>

              {/* Loan Amount */}
              <div className="mb-4">
                <label className="form-label d-flex justify-content-between">
                  <span>Loan Amount</span>
                  <span>
                    <FaIndianRupeeSign />
                    {loanAmount.toLocaleString()}
                  </span>
                </label>
                <input
                  type="range"
                  min="50000"
                  max="9000000"
                  step="10000"
                  className="form-range"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                />
              </div>

              {/* Interest Rate */}
              <div className="mb-4">
                <label className="form-label d-flex justify-content-between">
                  <span>Interest Rate</span>
                  <span>{interestRate}%</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  step="0.1"
                  className="form-range"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                />
              </div>

              {/* Loan Tenure */}
              <div className="mb-4">
                <label className="form-label d-flex justify-content-between">
                  <span>Loan Tenure</span>
                  <span>{tenure} years</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="30"
                  className="form-range"
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                />
              </div>

              {/* Calculate Button */}
              <button className="get-call-btn-property-home-page w-100">
                CALCULATE EMI
              </button>

              {/* EMI Result */}
              <div className="mt-4 text-center">
                <h6>Estimated Monthly EMI</h6>
                <h4 className="fw-bold text-success">
                  <FaIndianRupeeSign />

                  {Math.round(emi)}
                </h4>
              </div>
              <div className="mt-3 text-center">
                <p className="mb-1">
                  Principal Amount: <FaIndianRupeeSign />
                  {loanAmount.toLocaleString()}
                </p>
                <p className="mb-1">
                  Interest Amount: <FaIndianRupeeSign />
                  {Math.round(totalInterest)}
                </p>
                <p className="fw-bold">
                  Total Amount Payable: <FaIndianRupeeSign />
                  {Math.round(totalPayable)}
                </p>
              </div>

              {/* Pie Chart Section */}
            </div>
          </div>
          <div className="col-md-6">
            <div className="mt-4">
              <h6 className="text-center">Loan Distribution</h6>
              <Pie data={pieData} />
            </div>
          </div>
          <div className="col-md-12">
            <h4 className="configuration-txt mt-5">Properties You May Like</h4>
          </div>
          <div className="col-md-4">
            <div className="discounted-deal-property-div">
              <Link
                to={`https://homesdekho.com/property-detail/The-House-of-Abhinandan-Lodha-Naigaon`}
                className="view-detail-btn-property-home-txt"
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/homes-dekho.appspot.com/o/1756546577432HOABL.png?alt=media&token=d4db2268-2808-4c88-9ca5-6ad2fdcad9ce"
                  className="discounted-property-img img-fluid "
                  alt="https://firebasestorage.googleapis.com/v0/b/homes-dekho.appspot.com/o/1756546577432HOABL.png?alt=media&token=d4db2268-2808-4c88-9ca5-6ad2fdcad9ce"
                />
              </Link>
              <div className="disocunted-property-specs-div">
                <p className="discounted-property-name">
                  <Link
                    to={`https://homesdekho.com/property-detail/The-House-of-Abhinandan-Lodha-Naigaon`}
                    className="view-detail-btn-property-home-txt"
                  >
                    The House of Abhinandan Lodha Naigaon
                  </Link>
                </p>
                <p className="discounted-property-address-txt">
                  <FaLocationDot />
                  <span>Naigaon East, Mumbai</span>
                </p>
                <p className="discounted-property-BHK-txt">
                  <FaBuilding />
                  1, 2 BHK
                </p>
                <p className="discounted-property-price">
                  <FaIndianRupeeSign />
                  <span>35.50 L - 76.00 L</span>
                </p>
                <div className="property-devloper-btn-home-div">
                  <Link
                    to={
                      "https://homesdekho.com/property-detail/The-House-of-Abhinandan-Lodha-Naigaon"
                    }
                    className="view-detail-btn-property-home"
                  >
                    View Details
                  </Link>
                  <button
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target="#staticBackdrop"
                    className="get-call-btn-property-home-page"
                    onClick={() =>
                      handleButtonClick("The House of Abhinandan Lodha Naigaon")
                    }
                  >
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="discounted-deal-property-div">
              <Link
                to={`https://homesdekho.com/property-detail/Birla-Taranya`}
                className="view-detail-btn-property-home-txt"
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/homes-dekho.appspot.com/o/1771057558668taranya.webp?alt=media&token=e51b01dc-4e4e-4c02-8f81-86eda2f83b1e"
                  className="discounted-property-img img-fluid "
                  alt="Birla Taranya"
                />
              </Link>
              <div className="disocunted-property-specs-div">
                <p className="discounted-property-name">
                  <Link
                    to={`https://homesdekho.com/property-detail/Birla-Taranya`}
                    className="view-detail-btn-property-home-txt"
                  >
                    Birla Taranya
                  </Link>
                </p>
                <p className="discounted-property-address-txt">
                  <FaLocationDot />
                  <span>Thane Belapur Road</span>
                </p>
                <p className="discounted-property-BHK-txt">
                  <FaBuilding />
                  1, 2 BHK
                </p>
                <p className="discounted-property-price">
                  <FaIndianRupeeSign />
                  <span>90.00 L - 1.56 Cr.</span>
                </p>
                <div className="property-devloper-btn-home-div">
                  <Link
                    to={
                      "https://homesdekho.com/property-detail/Birla-Taranya"
                    }
                    className="view-detail-btn-property-home"
                  >
                    View Details
                  </Link>
                  <button
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target="#staticBackdrop"
                    className="get-call-btn-property-home-page"
                    onClick={() => handleButtonClick("Birla Estates Kalwa")}
                  >
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="discounted-deal-property-div">
              <Link
                to={`https://homesdekho.com/property-detail/Raj-Tulsi-Aawas`}
                className="view-detail-btn-property-home-txt"
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/homes-dekho.appspot.com/o/1739174562301Tulsi.avif?alt=media&token=35951be3-e1eb-4fd4-9058-7df1c62c8fe6"
                  className="discounted-property-img img-fluid "
                  alt="Raj Tulsi Aawas"
                />
              </Link>
              <div className="disocunted-property-specs-div">
                <p className="discounted-property-name">
                  <Link
                    to={`https://homesdekho.com/property-detail/Raj-Tulsi-Aawas`}
                    className="view-detail-btn-property-home-txt"
                  >
                    Raj Tulsi Aawas
                  </Link>
                </p>
                <p className="discounted-property-address-txt">
                  <FaLocationDot />
                  <span>Badlapur East</span>
                </p>
                <p className="discounted-property-BHK-txt">
                  <FaBuilding />
                  1, 2 BHK
                </p>
                <p className="discounted-property-price">
                  <FaIndianRupeeSign />
                  <span>25.00 L - 30.00 L</span>
                </p>
                <div className="property-devloper-btn-home-div">
                  <Link
                    to={
                      "https://homesdekho.com/property-detail/Raj-Tulsi-Aawas"
                    }
                    className="view-detail-btn-property-home"
                  >
                    View Details
                  </Link>
                  <button
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target="#staticBackdrop"
                    className="get-call-btn-property-home-page"
                    onClick={() => handleButtonClick("Raj Tulsi Aawas")}
                  >
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-3">
            <div className="lp-design-div">
              <div className="">
                <h4 className="configuration-txt">
                  Home Loan EMI Calculator - FAQs
                </h4>
              </div>
              <div class="accordion custome-accordian" id="accordionExample">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseOne"
                      aria-expanded="true"
                      aria-controls="collapseOne"
                    >
                      What is a Home Loan EMI?
                    </button>
                  </h2>
                  <div
                    id="collapseOne"
                    class="accordion-collapse collapse show"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      A Home Loan EMI (Equated Monthly Instalment) is a fixed
                      monthly payment made towards repaying your home loan. Each
                      EMI includes both principal and interest, ensuring that
                      the loan is cleared over the chosen tenure.
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseTwo"
                      aria-expanded="false"
                      aria-controls="collapseTwo"
                    >
                      What is a Home Loan payback schedule?
                    </button>
                  </h2>
                  <div
                    id="collapseTwo"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      An payback schedule is a detailed statement that breaks
                      down every EMI into its components. It shows: Outstanding
                      balance before each EMI Total EMI payable Portion of EMI
                      going towards principal repayment Portion of EMI going
                      towards interest payment Remaining loan balance after each
                      EMI
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseThree"
                      aria-expanded="false"
                      aria-controls="collapseThree"
                    >
                      How are the principal and interest distributed in EMIs?
                    </button>
                  </h2>
                  <div
                    id="collapseThree"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      In the initial years, a larger share of the EMI goes
                      towards paying interest, while a smaller portion reduces
                      the principal. Over time, this gradually shifts, and a
                      greater part of the EMI contributes to reducing the
                      principal.
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseFour"
                      aria-expanded="false"
                      aria-controls="collapseFour"
                    >
                      Can part-payments impact my EMI?
                    </button>
                  </h2>
                  <div
                    id="collapseFour"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      Yes. When you make part-payments, your outstanding loan
                      amount decreases. While your EMI value may remain the
                      same, a higher portion starts going towards principal
                      repayment, helping you close the loan faster and save on
                      interest costs.
                    </div>
                  </div>
                </div>

                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseSix"
                      aria-expanded="false"
                      aria-controls="collapseSix"
                    >
                      How can EMI payments reduce my tax liability?
                    </button>
                  </h2>
                  <div
                    id="collapseSix"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      Under the Income Tax Act, 1961, Indian taxpayers can claim
                      deductions on both the principal and interest components
                      of Home Loan EMIs (subject to applicable sections and
                      limits). This benefit helps reduce overall tax liability
                      while enabling home ownership.
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseSeven"
                      aria-expanded="false"
                      aria-controls="collapseSeven"
                    >
                      How does the Home Loan EMI Calculator help in planning?
                    </button>
                  </h2>
                  <div
                    id="collapseSeven"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionExample"
                  >
                    <div class="accordion-body">
                      The EMI Calculator is a simple yet powerful tool that:
                      Instantly calculates monthly EMIs based on loan amount,
                      tenure, and interest rate. Lets you adjust values easily
                      to find an EMI that fits your budget. Helps simulate
                      scenarios such as prepayments or tenure reduction.
                      Provides a clear repayment plan to support better
                      financial decisions.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup data={popupData} />
    </>
  );
};
export default EmiCalculator;
